import { readAgentAtWorkCard, readAgentCard, readCreditGiftCard, readInsightCard, readMeetingNotesCard, readMilestoneCard, readConnectorResultCard, readSocialPostPreviewCard } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { readProductCards } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { readBandCard, readBandOfferCard } from './band-cards';
import { readConnectorCard } from './connector-card';
import { readProfileLinkCard } from './profile-link-chat-card';
import { readGoalCard } from './goal-card';
import { readCreditsCard } from './credits-card';
import { readInviteCard } from './invite-card';
import { readPackShareChatCard } from './pack-share-chat-card';
import { readMeetingApprovalMarker } from './meeting-approval';
import { readOpportunityCard } from './opportunity-card';
import { readOutreachPreviewCard } from './outreach-preview-card';
import { readPostConnectedConnectorCard } from './post-connected-connector-card';
import { readProposalsCardMarker } from './proposals-card';
import { readShareOfferCard, readShareResponseCard } from './share-offer-card';
import { readMeetingHoldsCard, readSendHoldCard } from './send-hold';
import { readShopifyBatchCard, readShopifyBriefingCard } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { Message } from './use-chat';

/**
 * Every card a chat row can carry, read off one metadata object in one pass.
 *
 * It exists so no transport has to know the card kinds by name. A card used to be plumbed
 * individually through the SSE `messageEnd` branch, the duplexer push and the history mapping — and a
 * kind missing from any one of them stayed invisible until a reload. On the phones two actually were,
 * in opposite directions (`creditsCard` on Android, `postConnectedConnectorCard` on iOS), which is
 * what a hand-maintained list per site produces. Adding a card kind means adding it HERE and to
 * `CHAT_CARD_METADATA_KEYS` in common-lib.
 *
 * Every per-card reader is fail-closed on its own, so a malformed marker yields no card rather than a
 * broken one. Absent keys are omitted rather than set to `undefined`, so the result spreads straight
 * onto a message without erasing what is already there.
 *
 * Mirrors iOS's and Android's `ChatCards`.
 */
export type ChatCards = Pick<
  Message,
  | 'agentCard'
  | 'agentAtWorkCard'
  | 'milestoneCard'
  | 'creditGiftCard'
  | 'connectorCard'
  | 'profileLinkCard'
  | 'creditsCard'
  | 'inviteCard'
  | 'packShareCard'
  | 'opportunityCard'
  | 'insightCard'
  | 'productCards'
  | 'connectorResultCard'
  | 'postConnectedConnectorCard'
  | 'shopifyBatchCard'
  | 'shopifyBriefingCard'
  | 'outreachPreviewCard'
  | 'socialPostPreviewCard'
  | 'shareOfferCard'
  | 'shareResponseCard'
  | 'bandOfferCard'
  | 'bandCard'
  | 'goalCard'
  | 'meetingNotesCard'
  | 'bandCreated'
  | 'meetingApproval'
  | 'proposalsCard'
  | 'sendHoldCard'
  | 'meetingHoldsCard'
>;

/**
 * Read the cards off a row's `metadata`.
 *
 * The SSE `messageEnd` payload names its cards with the same keys the metadata does — the harness
 * spreads the persisted row's card metadata straight onto the event's `data` — so this reads that
 * event too, which is what lets a new card kind arrive with no change at the call sites.
 */
export function readChatCards(metadata: unknown): ChatCards {
  const cards: ChatCards = {};
  const shopifyBatchCard = readShopifyBatchCard((metadata as { shopifyBatchCard?: unknown } | undefined)?.shopifyBatchCard);
  if (shopifyBatchCard) cards.shopifyBatchCard = shopifyBatchCard;
  const shopifyBriefingCard = readShopifyBriefingCard((metadata as { shopifyBriefingCard?: unknown } | undefined)?.shopifyBriefingCard);
  if (shopifyBriefingCard) cards.shopifyBriefingCard = shopifyBriefingCard;
  const agentCard = readAgentCard(metadata as never);
  if (agentCard) cards.agentCard = agentCard;
  const agentAtWorkCard = readAgentAtWorkCard(metadata as never);
  if (agentAtWorkCard) cards.agentAtWorkCard = agentAtWorkCard;
  const milestoneCard = readMilestoneCard(metadata as never);
  if (milestoneCard) cards.milestoneCard = milestoneCard;
  const creditGiftCard = readCreditGiftCard(metadata as never);
  if (creditGiftCard) cards.creditGiftCard = creditGiftCard;
  const connectorCard = readConnectorCard(metadata);
  if (connectorCard) cards.connectorCard = connectorCard;
  const profileLinkCard = readProfileLinkCard(metadata);
  if (profileLinkCard) cards.profileLinkCard = profileLinkCard;
  const creditsCard = readCreditsCard(metadata);
  if (creditsCard) cards.creditsCard = creditsCard;
  const inviteCard = readInviteCard(metadata);
  if (inviteCard) cards.inviteCard = inviteCard;
  const packShareCard = readPackShareChatCard(metadata);
  if (packShareCard) cards.packShareCard = packShareCard;
  const opportunityCard = readOpportunityCard(metadata);
  if (opportunityCard) cards.opportunityCard = opportunityCard;
  const insightCard = readInsightCard(metadata as never);
  if (insightCard) cards.insightCard = insightCard;
  const productCards = readProductCards(metadata as never);
  if (productCards) cards.productCards = productCards;
  const connectorResultCard = readConnectorResultCard(metadata);
  if (connectorResultCard) cards.connectorResultCard = connectorResultCard;
  const postConnectedConnectorCard = readPostConnectedConnectorCard(metadata);
  if (postConnectedConnectorCard) cards.postConnectedConnectorCard = postConnectedConnectorCard;
  const outreachPreviewCard = readOutreachPreviewCard(metadata);
  if (outreachPreviewCard) cards.outreachPreviewCard = outreachPreviewCard;
  const socialPostPreviewCard = readSocialPostPreviewCard(metadata as never);
  if (socialPostPreviewCard) cards.socialPostPreviewCard = socialPostPreviewCard;
  const shareOfferCard = readShareOfferCard(metadata);
  if (shareOfferCard) cards.shareOfferCard = shareOfferCard;
  const shareResponseCard = readShareResponseCard(metadata);
  if (shareResponseCard) cards.shareResponseCard = shareResponseCard;
  const bandOfferCard = readBandOfferCard(metadata);
  if (bandOfferCard) cards.bandOfferCard = bandOfferCard;
  const bandCard = readBandCard(metadata);
  if (bandCard) cards.bandCard = bandCard;
  const goalCard = readGoalCard(metadata);
  if (goalCard) cards.goalCard = goalCard;
  const meetingNotesCard = readMeetingNotesCard(metadata as never);
  if (meetingNotesCard) cards.meetingNotesCard = meetingNotesCard;
  if ((metadata as { bandCreated?: unknown } | null | undefined)?.bandCreated === true) cards.bandCreated = true;
  const meetingApproval = readMeetingApprovalMarker(metadata);
  if (meetingApproval) cards.meetingApproval = meetingApproval;
  const proposalsCard = readProposalsCardMarker(metadata);
  if (proposalsCard) cards.proposalsCard = proposalsCard;
  const sendHoldCard = readSendHoldCard(metadata);
  if (sendHoldCard) cards.sendHoldCard = sendHoldCard;
  const meetingHoldsCard = readMeetingHoldsCard(metadata);
  if (meetingHoldsCard) cards.meetingHoldsCard = meetingHoldsCard;
  return cards;
}

/**
 * Whether a row is the "first workspace exists" moment the onboarding-finished funnel events (GTM and
 * AppsFlyer) key on: the workspace milestone card, or the reply of the turn that created a band. The band
 * creates its space without that card, so without the second arm a band user never finished onboarding
 * in either funnel. Mirrors iOS's and Android's `isFirstWorkspaceMilestone`.
 */
export function isFirstWorkspaceMilestone(cards: Pick<ChatCards, 'milestoneCard' | 'bandCreated'>): boolean {
  return cards.milestoneCard?.kind === 'workspace' || cards.bandCreated === true;
}

/**
 * Patch a republished row's cards onto the row already on screen (matched by id OR persisted
 * `finalId`); no-op when the row is gone or the republish carried no cards.
 *
 * A card that arrived WINS, a card that is absent changes nothing — `readChatCards` omits absent keys,
 * so the spread cannot erase one. That rule matters more here than for `choices`: several of these
 * cards ARE the action (a connect link, an outreach approval), so erasing one leaves chips pointing at
 * something the reader can no longer see.
 *
 * This is the fallback path for a card the in-band `messageEnd` could not carry — one stamped on the
 * row after it was persisted (`creditsCard`), or a reply that settled on another device or an async turn.
 */
export function patchChatCardsById<M extends { id?: string; finalId?: string }>(messages: M[], messageId: string, cards: ChatCards): M[] {
  if (!hasChatCards(cards)) return messages;
  const matches = (m: M) => m.id === messageId || m.finalId === messageId;
  if (!messages.some(matches)) return messages;
  return messages.map((m) => (matches(m) ? { ...m, ...cards } : m));
}

/** True when a row carries any card at all — the generic form of every per-card presence check. */
export function hasChatCards(cards: ChatCards): boolean {
  return Object.keys(cards).length > 0;
}

/**
 * A card the harness WROTE rather than streamed — the agent-join orbit, an agent at work, a setup
 * milestone, a credit gift. Such a row has no SSE counterpart at all, so a client that drops assistant
 * rows mid-turn must let these through: dropping one loses the row permanently, where dropping a
 * streamed reply's republish only loses a duplicate.
 *
 * Deliberately NOT "carries any card": a reply's own cards ride the streamed row and arrive in-band on
 * `messageEnd`. Mirrors common-lib's `isPresentationCardRow` and the two phones' `hasPresentationCard`.
 */
export function hasPresentationCard(cards: ChatCards): boolean {
  // `shareResponseCard` belongs here and `shareOfferCard` does not, and the difference is the design's:
  // a bare emoji gets a templated row with NO llm turn behind it (§8), so that row has no streamed
  // counterpart to be a duplicate of — dropping it mid-turn loses the reaction permanently. The offer
  // rides an ordinary reply and arrives in-band like every other reply's card.
  //
  // Note this is NOT the same question common-lib's `isPresentationCardRow` answers. That one decides
  // what to hide from the MODEL, and the response row is deliberately not hidden there: it is the only
  // record that the reaction happened, so stripping it would leave the agent never knowing.
  return Boolean(cards.agentCard || cards.agentAtWorkCard || cards.milestoneCard || cards.creditGiftCard || cards.shareResponseCard || cards.shopifyBatchCard?.pushOnly);
}

export function latestShopifyBatchCards<T extends Pick<ChatCards, 'shopifyBatchCard'>>(messages: readonly T[]): T[] {
  const latest = new Set<string>();
  return [...messages].reverse().map(message => {
    const id = message.shopifyBatchCard?.batchId;
    if (!id) return message;
    if (latest.has(id)) return { ...message, shopifyBatchCard: undefined };
    latest.add(id);
    return message;
  }).reverse();
}
